import { useCallback, useEffect, useRef, useState } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboardingTour.css';
import { useAuth } from '@/components/AuthProvider';
import { useCabinetMemberMe } from '@/hooks/useCabinetMemberMe';
import { useManualPaywall } from '@/hooks/useManualPaywall';
import { usePostHog } from '@/hooks/usePostHog';
import {
  DOSSIER_TABS,
  evaluateStep,
  FIRST_STEP_ID,
  GESTION_MODE,
  getNextStepId,
  getStep,
  getStepIndex,
  PIECES_TAB_MARKER,
  REDIRECT_BUBBLES,
  TAB_LABEL_ATTRIBUTE,
  fillTabLabels,
  type TourTabKey,
  TOUR_STEPS,
  type TourStepId,
} from './tourSteps';
import {
  ONBOARDING_TOUR_AUDIENCE,
  isTourVisible,
  readTourProgress,
  shouldAutoStartTour,
  subscribeTourProgress,
  writeTourProgress,
  type TourProgress,
} from './tourProgress';
import { useCloseOnboardingTour } from './useCloseOnboardingTour';
import { useOnboardingTourFlag } from './useOnboardingTourFlag';

/** Cadence d'observation de la page. */
const TICK_MS = 250;
/**
 * Durée pendant laquelle l'ancre doit rester affichable avant d'ouvrir la
 * bulle. Évite qu'elle clignote entre deux fenêtres enchaînées (l'import de
 * pièces en ouvre trois de suite).
 */
const SETTLE_MS = 500;
/** Ancre introuvable depuis ce délai : retour à l'étape `fallback`. */
const FALLBACK_MS = 2500;
/**
 * Surcouches Radix ouvertes (Dialog, AlertDialog, Sheet, Popover, menus). Elles
 * capturent le focus et les clics : une bulle affichée par-dessus serait
 * inutilisable, le parcours se met donc en retrait tant qu'elles sont là.
 */
const MODAL_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  // Menus et listes déroulantes : rendus dans un portail hors de l'ancre,
  // ils seraient inertes sous le voile du parcours.
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
].join(', ');
/** Sous cette largeur la barre latérale disparaît et les onglets deviennent une liste. */
const DESKTOP_QUERY = '(min-width: 768px)';

function findVisible(selector: string): HTMLElement | null {
  // Plusieurs éléments peuvent porter la même ancre (la liste des dossiers est
  // rendue en version mobile ET bureau) : on prend le premier réellement affiché.
  for (const element of document.querySelectorAll<HTMLElement>(selector)) {
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return element;
  }
  return null;
}

/**
 * Libellé qu'affiche un onglet du dossier, lu à l'écran : il change avec la
 * matière du dossier, que le parcours ne connaît pas. `null` si l'onglet n'est
 * pas affiché. La bulle est rendue en HTML par driver.js : le libellé est
 * échappé avant d'y être inséré.
 */
function readTabLabel(tab: TourTabKey): string | null {
  const label = findVisible(`[${TAB_LABEL_ATTRIBUTE}="${tab}"]`)?.textContent?.trim();
  if (!label) return null;
  return label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Exécute `highlight` puis remet à leur position horizontale les conteneurs
 * que driver.js aurait fait défiler.
 *
 * WHY : driver.js appelle `scrollIntoView({ inline: 'center' })` sur l'ancre.
 * La page du dossier a un conteneur `overflow-hidden` dont le contenu déborde
 * (la rangée d'onglets) : il se décale alors à l'horizontale, sans barre de
 * défilement pour revenir, et toute la page reste coupée à gauche.
 */
function highlightWithoutHorizontalScroll(element: HTMLElement | null, highlight: () => void): void {
  const before: Array<[HTMLElement, number]> = [];
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    before.push([node, node.scrollLeft]);
  }
  highlight();
  for (const [node, scrollLeft] of before) {
    if (node.scrollLeft !== scrollLeft) node.scrollLeft = scrollLeft;
  }
}

function rectKey(element: HTMLElement): string {
  const { top, left, width, height } = element.getBoundingClientRect();
  return `${Math.round(top)}:${Math.round(left)}:${Math.round(width)}:${Math.round(height)}`;
}

function useTourProgress(userId: string | undefined): TourProgress | null {
  const [progress, setProgress] = useState<TourProgress | null>(() =>
    userId ? readTourProgress(userId) : null,
  );
  useEffect(() => {
    if (!userId) {
      setProgress(null);
      return;
    }
    const sync = () => setProgress(readTourProgress(userId));
    sync();
    return subscribeTourProgress(sync);
  }, [userId]);
  return progress;
}

/**
 * Parcours d'onboarding, monté une fois dans `DashboardLayout`.
 *
 * Le composant ne rend rien : il observe la page à intervalle régulier et
 * demande à driver.js de surligner l'ancre de l'étape courante quand elle est
 * affichable. Il n'utilise pas le séquenceur de driver.js, car les étapes
 * dépendent d'actions réelles (dossier créé, pièce importée, navigation) et
 * non d'un simple bouton « Suivant ».
 *
 * `blocked` : vrai quand le layout affiche une fenêtre globale (essai expiré,
 * paywall…), même contrat que `AnnouncementGate`.
 */
export function OnboardingTourGate({ blocked }: { blocked: boolean }) {
  const forced = useOnboardingTourFlag();
  const { user } = useAuth();
  const userId = user?.id;
  const userCreatedAt = user?.created_at;
  const { trackEvent } = usePostHog();
  // Le paywall manuel est un voile autonome que le layout ne connaît pas.
  const { isPaywallActive } = useManualPaywall();
  const progress = useTourProgress(userId);
  const closeOnServer = useCloseOnboardingTour();
  // `trackEvent` et la mutation sont recréés à chaque rendu ; des refs évitent
  // de relancer l'observation de la page pour autant.
  const trackRef = useRef(trackEvent);
  trackRef.current = trackEvent;
  const closeRef = useRef(closeOnServer.mutate);
  closeRef.current = closeOnServer.mutate;
  const manualRef = useRef(false);
  manualRef.current = progress?.manual === true;

  const { data: member } = useCabinetMemberMe();
  // `undefined` tant que le serveur n'a pas répondu : aucune décision avant.
  const closedOnServer = member ? member.onboarding_tour_closed_at != null : undefined;

  // Démarrage d'office : compte créé depuis la date de lancement (ou drapeau),
  // jamais clos côté serveur, et aucune progression dans ce navigateur.
  useEffect(() => {
    if (!userId || progress || closedOnServer === undefined) return;
    if (!shouldAutoStartTour({ userCreatedAt, forced, closedOnServer, audience: ONBOARDING_TOUR_AUDIENCE })) return;
    writeTourProgress(userId, { status: 'active', stepId: FIRST_STEP_ID });
    trackRef.current('onboarding_tour_started', { feature: 'onboarding_tour' });
  }, [userId, progress, closedOnServer, userCreatedAt, forced]);

  const goTo = useCallback(
    (stepId: TourStepId | null, from: TourStepId) => {
      if (!userId) return;
      if (stepId === null) {
        writeTourProgress(userId, { status: 'completed', stepId: from });
        closeRef.current();
        trackRef.current('onboarding_tour_completed', { feature: 'onboarding_tour' });
        return;
      }
      writeTourProgress(
        userId,
        manualRef.current ? { status: 'active', stepId, manual: true } : { status: 'active', stepId },
      );
    },
    [userId],
  );

  const dismiss = useCallback(
    (from: TourStepId) => {
      if (!userId) return;
      writeTourProgress(userId, { status: 'dismissed', stepId: from });
      closeRef.current();
      trackRef.current('onboarding_tour_dismissed', {
        feature: 'onboarding_tour',
        step_id: from,
        step_index: getStepIndex(from),
      });
    },
    [userId],
  );

  const activeStepId =
    !blocked && !isPaywallActive && progress && isTourVisible(progress, closedOnServer)
      ? progress.stepId
      : null;

  useEffect(() => {
    if (!activeStepId) return;
    const step = getStep(activeStepId);
    if (!step) return;

    let driverInstance: Driver | null = null;
    let shownElement: HTMLElement | null = null;
    let shownRect = '';
    let removeClickListener: (() => void) | null = null;
    let showableSince: number | null = null;
    let missingSince: number | null = null;
    let viewTracked = false;

    const hide = () => {
      removeClickListener?.();
      removeClickListener = null;
      driverInstance?.destroy();
      driverInstance = null;
      shownElement = null;
    };

    const advance = () => {
      hide();
      goTo(getNextStepId(step.id), step.id);
    };

    // `redirect` : bulle de rappel posée sur l'onglet ou la bascule de mode à
    // cliquer pour revenir à l'écran de l'étape ; elle ne franchit rien.
    const show = (element: HTMLElement | null, redirect?: keyof typeof REDIRECT_BUBBLES) => {
      hide();
      const isLast = getNextStepId(step.id) === null;
      const hasNextButton = !redirect && (step.advance === 'next' || step.skippable === true);
      const bubble = redirect ? REDIRECT_BUBBLES[redirect] : step;
      driverInstance = driver({
        // Un clic à côté ou la touche Échap ne doivent pas faire perdre le
        // parcours par accident : seule la croix le quitte.
        overlayClickBehavior: () => undefined,
        allowKeyboardControl: false,
        overlayOpacity: 0.45,
        stagePadding: 6,
        stageRadius: 8,
        popoverClass: step.large ? 'hector-tour-popover hector-tour-large' : 'hector-tour-popover',
      });
      const instance = driverInstance;
      highlightWithoutHorizontalScroll(element, () =>
        instance.highlight({
          element: element ?? undefined,
          // Sur une étape d'information, l'élément surligné ne doit pas réagir :
          // un clic dessus changerait d'écran et ferait disparaître l'ancre suivante.
          disableActiveInteraction: !redirect && step.advance === 'next' && !step.interactive,
          popover: {
            title: bubble.title,
            description: fillTabLabels(bubble.description, readTabLabel),
            side: redirect ? 'bottom' : step.side,
            showButtons: hasNextButton ? ['next', 'close'] : ['close'],
            nextBtnText: isLast ? 'Terminer' : step.advance === 'next' ? 'Suivant' : 'Passer',
            showProgress: true,
            progressText: `Étape ${getStepIndex(step.id) + 1} sur ${TOUR_STEPS.length}`,
            onPopoverRender: (popover) => {
              // driver.js nomme sa croix « Close », en anglais.
              popover.closeButton.setAttribute('aria-label', 'Quitter le tutoriel');
              popover.closeButton.setAttribute('title', 'Quitter le tutoriel');
            },
            onNextClick: advance,
            onCloseClick: () => {
              hide();
              dismiss(step.id);
            },
          },
        }),
      );
      // Le défilement restauré a déplacé l'ancre : recaler voile et bulle.
      instance.refresh();
      shownElement = element;
      shownRect = element ? rectKey(element) : '';

      if (redirect) return;

      if (element && step.advance === 'anchor-click') {
        // Capture : on avance avant que le clic n'ouvre la fenêtre visée, pour
        // que le voile du parcours ne reste pas affiché derrière elle.
        const onClick = () => advance();
        element.addEventListener('click', onClick, { capture: true, once: true });
        removeClickListener = () => element.removeEventListener('click', onClick, { capture: true });
      }

      if (!viewTracked) {
        viewTracked = true;
        trackRef.current('onboarding_tour_step_viewed', {
          feature: 'onboarding_tour',
          step_id: step.id,
          step_index: getStepIndex(step.id),
        });
      }
    };

    const tick = () => {
      const now = Date.now();
      const desktop = window.matchMedia(DESKTOP_QUERY).matches;
      const element = step.anchor ? findVisible(step.anchor) : null;
      // Où ramener l'utilisateur s'il a quitté l'écran de l'étape : la rangée
      // d'onglets tant qu'il est en « Gestion des pièces », sinon la bascule de mode.
      const tabs = step.dossierTab === 'pieces' ? findVisible(DOSSIER_TABS) : null;
      const redirectTarget = step.dossierTab ? tabs ?? findVisible(GESTION_MODE) : null;
      const verdict = evaluateStep(step, {
        pathname: window.location.pathname,
        location: window.location.pathname + window.location.search,
        modalOpen: document.querySelector(MODAL_SELECTOR) !== null,
        anchorVisible: element !== null,
        doneVisible: step.doneWhenVisible ? findVisible(step.doneWhenVisible) !== null : false,
        screenVisible: step.dossierTab
          ? findVisible(step.dossierTab === 'pieces' ? PIECES_TAB_MARKER : DOSSIER_TABS) !== null
          : undefined,
        redirectVisible: redirectTarget !== null,
      });

      if (verdict === 'advance') {
        advance();
        return;
      }

      if ((verdict === 'show' || verdict === 'redirect') && desktop) {
        missingSince = null;
        showableSince ??= now;
        if (now - showableSince < SETTLE_MS) return;
        const target = verdict === 'redirect' ? redirectTarget : element;
        if (!driverInstance || shownElement !== target) {
          show(target, verdict === 'redirect' ? (tabs ? 'pieces' : 'gestion') : undefined);
        } else if (target && rectKey(target) !== shownRect) {
          // L'ancre a bougé (panneau redimensionné, barre latérale repliée).
          shownRect = rectKey(target);
          driverInstance.refresh();
        }
        return;
      }

      showableSince = null;
      if (driverInstance) hide();

      if (verdict === 'missing' && (step.fallback || step.skipIfMissing)) {
        missingSince ??= now;
        // Bulle déjà affichée puis ancre disparue : l'utilisateur a refermé ce
        // qu'elle commentait (le panneau de tamponnage). Inutile d'attendre.
        const closedByUser = viewTracked && step.skipIfMissing === true;
        if (closedByUser || now - missingSince >= FALLBACK_MS) {
          goTo(step.fallback ?? getNextStepId(step.id), step.id);
        }
      } else {
        missingSince = null;
      }
    };

    const interval = window.setInterval(tick, TICK_MS);
    tick();
    return () => {
      window.clearInterval(interval);
      hide();
    };
  }, [activeStepId, goTo, dismiss]);

  return null;
}
